import { api } from '@/lib/api'

/** @typedef {import('@/types/api').Sale} Sale */
/** @typedef {import('@/types/api').SaleLine} SaleLine */
/** @typedef {import('@/types/api').ServiceAddonOption} ServiceAddonOption */
/** @typedef {import('@/types/api').SuspendedSaleSummary} SuspendedSaleSummary */
/** @typedef {import('@/types/api').PosReceipt} PosReceipt */
/** @typedef {import('@/types/api').PosTaxInvoice} PosTaxInvoice */
/** @typedef {import('@/types/api').PosServiceSlips} PosServiceSlips */

/**
 * @param {Partial<{ customer_id: number | null, gender_price: 'ladies' | 'gents', notes: string }>} payload
 * @returns {Promise<Sale>}
 */
export async function createSale(payload = {}) {
  const { data } = await api.post('/sales', payload)
  return data.data.sale
}

/**
 * @param {number} saleId
 * @returns {Promise<Sale>}
 */
export async function fetchSale(saleId) {
  const { data } = await api.get(`/sales/${saleId}`)
  return data.data.sale
}

/**
 * @param {number} saleId
 * @param {{ gender_price?: 'ladies' | 'gents', customer_id?: number | null, notes?: string | null }} payload
 * @returns {Promise<Sale>}
 */
export async function updateSale(saleId, payload) {
  const { data } = await api.patch(`/sales/${saleId}`, payload)
  return data.data.sale
}

/**
 * @param {number} saleId
 * @param {{ line_type: 'service' | 'product', service_id?: number, product_id?: number, quantity?: number, stylist_id?: number | null }} payload
 * @returns {Promise<{ line: SaleLine, sale: Sale }>}
 */
export async function addSaleLine(saleId, payload) {
  const { data } = await api.post(`/sales/${saleId}/lines`, payload)
  return {
    line: data.data.line,
    sale: data.data.sale,
  }
}

/**
 * @param {number} saleId
 * @param {number} lineId
 * @param {{
 *   stylist_id?: number | null,
 *   stylist_split_percent?: number | null,
 *   quantity?: number,
 *   unit_price?: number,
 *   line_status?: 'waiting' | 'in_chair' | 'done',
 *   duration?: number,
 *   notes?: string | null,
 * }} payload
 * @returns {Promise<{ line: SaleLine, sale: Sale }>}
 */
export async function updateSaleLine(saleId, lineId, payload) {
  const { data } = await api.patch(`/sales/${saleId}/lines/${lineId}`, payload)
  return {
    line: data.data.line,
    sale: data.data.sale,
  }
}

/**
 * @param {number} saleId
 * @param {number} parentLineId
 * @param {{ addon_service_id: number, quantity?: number }} payload
 * @returns {Promise<{ line: SaleLine, sale: Sale }>}
 */
export async function addSaleAddon(saleId, parentLineId, payload) {
  const { data } = await api.post(`/sales/${saleId}/lines/${parentLineId}/addons`, payload)
  return {
    line: data.data.line,
    sale: data.data.sale,
  }
}

/**
 * @param {number} saleId
 * @param {number} addonLineId
 * @returns {Promise<Sale>}
 */
export async function removeSaleAddon(saleId, addonLineId) {
  const { data } = await api.delete(`/sales/${saleId}/lines/${addonLineId}/addon`)
  return data.data.sale
}

/**
 * @param {number} serviceId
 * @returns {Promise<ServiceAddonOption[]>}
 */
export async function fetchServiceAddons(serviceId) {
  const { data } = await api.get(`/services/${serviceId}/addons`)
  return data.data.addons
}

/**
 * @param {number} saleId
 * @param {{ tip_amount?: number, cash_tendered?: number, payments: Array<{ payment_method_id: number, amount: number, reference?: string }> }} payload
 * @returns {Promise<{ sale: Sale, changeDue: number | null }>}
 */
export async function paySale(saleId, payload) {
  const { data } = await api.post(`/sales/${saleId}/pay`, payload)
  return {
    sale: data.data.sale,
    changeDue: data.data.change_due ?? null,
  }
}

/**
 * @returns {Promise<SuspendedSaleSummary[]>}
 */
export async function fetchSuspendedSales() {
  const { data } = await api.get('/sales/suspended')
  return data.data.sales
}

/**
 * @param {number} saleId
 * @returns {Promise<Sale>}
 */
export async function suspendSale(saleId) {
  const { data } = await api.post(`/sales/${saleId}/suspend`)
  return data.data.sale
}

/**
 * @param {number} saleId
 */
export async function enqueueSaleToWalkinQueue(saleId) {
  const { data } = await api.post(`/sales/${saleId}/walkin-queue`)
  return data.data.entry
}

/**
 * @param {number} saleId
 * @returns {Promise<Sale>}
 */
export async function recallSale(saleId) {
  const { data } = await api.post(`/sales/${saleId}/recall`)
  return data.data.sale
}

/**
 * @returns {Promise<import('@/types/api').LastCompletedSaleSummary | null>}
 */
export async function fetchLastCompletedSale() {
  try {
    const { data } = await api.get('/sales/last-completed')
    return data.data.sale
  } catch (error) {
    if (error?.response?.status === 404) {
      return null
    }
    throw error
  }
}

/**
 * @param {number} saleId
 * @returns {Promise<PosReceipt>}
 */
export async function fetchSaleReceipt(saleId) {
  const { data } = await api.get(`/sales/${saleId}/receipt`)
  return data.data.receipt
}

/**
 * @param {number} saleId
 * @returns {Promise<PosTaxInvoice>}
 */
export async function fetchSaleTaxInvoice(saleId) {
  const { data } = await api.get(`/sales/${saleId}/tax-invoice`)
  return data.data.tax_invoice
}

/**
 * @param {number} saleId
 * @returns {Promise<PosServiceSlips>}
 */
export async function fetchSaleServiceSlips(saleId) {
  const { data } = await api.get(`/sales/${saleId}/service-slips`)
  return data.data.service_slips
}

/**
 * @param {number} saleId
 * @param {number} lineId
 * @returns {Promise<Sale>}
 */
export async function voidSaleLine(saleId, lineId) {
  const { data } = await api.post(`/sales/${saleId}/lines/${lineId}/void`)
  return data.data.sale
}

/**
 * @param {number} saleId
 * @param {{ reason: string }} payload
 * @returns {Promise<Sale>}
 */
export async function voidSale(saleId, payload) {
  const { data } = await api.post(`/sales/${saleId}/void`, payload)
  return data.data.sale
}

/**
 * @param {{ date?: string, q?: string }} [params]
 * @returns {Promise<import('@/types/api').Appointment[]>}
 */
export async function lookupPosAppointments(params = {}) {
  const { data } = await api.get('/sales/appointment-lookup', { params })
  return data.data.appointments
}

/**
 * @param {number} saleId
 * @param {number} appointmentId
 * @returns {Promise<{ sale: Sale, appointment: import('@/types/api').Appointment }>}
 */
export async function loadAppointmentToSale(saleId, appointmentId) {
  const { data } = await api.post(`/sales/${saleId}/load-appointment`, {
    appointment_id: appointmentId,
  })

  return {
    sale: data.data.sale,
    appointment: data.data.appointment,
  }
}

/**
 * @param {number} saleId
 * @param {number} lineId
 * @param {{ type: 'percent' | 'flat', value: number, discount_reason_id: number, manager_email?: string, manager_password?: string }} payload
 * @returns {Promise<{ line: SaleLine, sale: Sale }>}
 */
export async function applyLineDiscount(saleId, lineId, payload) {
  const { data } = await api.post(`/sales/${saleId}/lines/${lineId}/discount`, payload)
  return {
    line: data.data.line,
    sale: data.data.sale,
  }
}

/**
 * @param {number} saleId
 * @param {{ type: 'percent' | 'flat', value: number, discount_reason_id: number, manager_email?: string, manager_password?: string }} payload
 * @returns {Promise<Sale>}
 */
export async function applyHeaderDiscount(saleId, payload) {
  const { data } = await api.post(`/sales/${saleId}/header-discount`, payload)
  return data.data.sale
}

/**
 * @param {number} saleId
 * @param {{ code: string, manager_email?: string, manager_password?: string }} payload
 * @returns {Promise<Sale>}
 */
export async function applyPromoCode(saleId, payload) {
  const { data } = await api.post(`/sales/${saleId}/promo-code`, payload)
  return data.data.sale
}

/**
 * @param {number} saleId
 * @returns {Promise<Sale>}
 */
export async function removePromoCode(saleId) {
  const { data } = await api.delete(`/sales/${saleId}/promo-code`)
  return data.data.sale
}

/**
 * @param {number} saleId
 * @param {{ points: number }} payload
 * @returns {Promise<Sale>}
 */
export async function applyLoyaltyRedemption(saleId, payload) {
  const { data } = await api.post(`/sales/${saleId}/loyalty-redeem`, payload)
  return data.data.sale
}

/**
 * @param {number} saleId
 * @returns {Promise<Sale>}
 */
export async function removeLoyaltyRedemption(saleId) {
  const { data } = await api.delete(`/sales/${saleId}/loyalty-redeem`)
  return data.data.sale
}
